import axios from "../../api/axios";
import { encryptToken } from '../../utils/tokenUtils';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

export const PENDING_OTP_KEY = "pendingOtpEmail";

const AuthService = {
  isAuthenticated: () => {
    return !!localStorage.getItem("authToken");
  },

  // status = 1 => success -> OTP sent -> OTP verified -> Dashboard
  // status = 2 => OTP sent -> OTP verified -> error -> if refresh -> login
  login: async (email, password) => {
    try {
      const response = await axios.post("/login", { email, password });
      const { message, success } = response.data ?? {};
      return {
        success: Boolean(success),
        message: getApiErrorMessage(
          { message },
          success ? 'Login successful' : 'Login failed. Please check your credentials.'
        ),
      };
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        'Login failed. Please check your credentials.'
      );
      return { success: false, message: errorMessage };
    }
  },

  storeToken: (token) => {
    if (!token) return;
    const encryptedToken = encryptToken(token);
    localStorage.setItem("authToken", `encrypted:${encryptedToken}`);
    localStorage.setItem("authenticated", true);
    sessionStorage.removeItem(PENDING_OTP_KEY);
  },

  /** Clear stale session before OTP step without dropping pending OTP state. */
  beginOtpFlow: (email) => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authenticated");
    sessionStorage.setItem(PENDING_OTP_KEY, email);
  },

  loginOtp: async (user, otp) => {
    try {
      const response = await axios.post("/loginOtp", { otp: otp, email: user });
      const { message, success, token } = response.data ?? {};

      if (success) {
        AuthService.storeToken(token);
      }

      return {
        success: Boolean(success),
        token,
        message: getApiErrorMessage(
          { message },
          success ? 'Verification successful' : 'Invalid or expired code. Please try again.'
        ),
      };
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = getApiErrorMessage(
        error,
        'Error verifying PIN. Please try again.'
      );
      return { success: false, message: errorMessage };
    }
  },

  getUser: async () => {
    try {
      const response = await axios.get("/me");
      const { user, role } = response.data;
      return { success: true, user, role };
    } catch (error) {
      console.error("Failed to fetch user data", error);
      return { success: false, error };
    }
  },

  verifyEmail: async (token) => {
    try {
      const response = await axios.get(`/email/verify/${token}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error("Email verification failed", error);
      return { success: false, error };
    }
  },

  forgotPassword: async (email, appUrl) => {
    try {
      const response = await axios.post('/reset-password-request', {
        email,
        appUrl: appUrl || (typeof window !== 'undefined' ? window.location.origin : undefined),
      });
      const { message, success } = response.data ?? {};
      return {
        success: success !== false,
        message: getApiErrorMessage(
          { message },
          'If that email exists, a password reset link has been sent.'
        ),
      };
    } catch (error) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Failed to send password reset email. Please try again.'
        ),
      };
    }
  },

  resetPassword: async (token, password, passwordConfirmation) => {
    try {
      const response = await axios.post('/reset-password-submit', {
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      const { message, success } = response.data ?? {};
      return {
        success: success !== false,
        message: getApiErrorMessage(
          { message },
          'Password has been reset successfully.'
        ),
      };
    } catch (error) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Failed to reset password. The link may be invalid or expired.'
        ),
      };
    }
  },

  checkPassword: async (password) => {
    try {
      const response = await axios.post('users/check_password', {
        password: password
      });
      console.log("response from service", response.data.isPasswordCorrect)
      return response.data.isPasswordCorrect;
      
    } catch (error) {
      console.error("Password check failed", error);
      return false;
    }
  },

  getSecuritySettings: async () => {
    try {
      const response = await axios.get('users/security_settings');
      // console.log("response from service", response)
      return response.data;
    } catch (error) {
      console.error("Failed to get security settings", error);
      return null;
    }
  },

  getBankDetails: async () => {
    try {
      const response = await axios.get('users/bank_details');
      return { success: true, data: response.data.bankDetails };
    } catch (error) {
      console.error('Error fetching bank details', error);
      return { success: false, error };
    }
  },

  updateSecurityLayers: async (newPassword, isEmailOTP, isSmsOTP, isPinOTP, pinValue) => {
    try {
      const response = await axios.post('users/update_security_layers', {
        new_password: newPassword,
        isEmailOTP: isEmailOTP,
        isSmsOTP: isSmsOTP,
        isPinOTP: isPinOTP,
        pin: pinValue
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update security layers", error);
      return null;
    }
  },

  getDashboardData: async () => {
    try {
      const response = await axios.get('/dashboard_data');
      const body = response.data ?? {};
      const payload = body.data ?? body;

      return { success: true, data: payload };
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
      return { success: false, error };
    }
  },

  logout: async ({ revokeOnServer = true } = {}) => {
    if (revokeOnServer && localStorage.getItem('authToken')) {
      try {
        await axios.post('/logout');
      } catch (error) {
        if (!error?.response || error.response.status >= 500) {
          console.warn('Logout API call failed:', getApiErrorMessage(error, 'Logout failed'));
        }
      }
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("authenticated");
    sessionStorage.removeItem(PENDING_OTP_KEY);
  },

  hasPendingOtp: () => Boolean(sessionStorage.getItem(PENDING_OTP_KEY)),

  getPendingOtpEmail: () => sessionStorage.getItem(PENDING_OTP_KEY) || '',
};

export default AuthService;